import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightLeft, MoreHorizontal } from 'lucide-react';
import type { WorkHandoff } from '@shared/types/thread-work';
import { Button } from '@/components/ui/Button';
import { usePopover } from '@/components/apps/usePopover';
import { DENSE_ACTION_CLASS } from '@/components/work/WorkActionControl';
import { closeHandoff } from '@/lib/work-handoffs';
import { RowIdentity } from './work-row-visuals';

const COMMAND_CLASS = `inline-flex min-h-8 items-center rounded-lg px-3 py-1 text-xs font-semibold leading-4 ${DENSE_ACTION_CLASS}`;

/**
 * A task a colleague passed to me (relais): who from, about what, their word.
 * « Ouvrir » leads to its context; « Terminé » ends it (the task is done for
 * them too); the menu sends it back with a reply.
 */
export const TodayHandoffRow = ({ handoff, fromName, onChange }: {
  handoff: WorkHandoff;
  fromName: string;
  onChange?: () => void;
}) => {
  const menu = usePopover();
  const [replying, setReplying] = useState(false);
  const [reply, setReply] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const close = async (outcome: 'done' | 'returned') => {
    setPending(true);
    setError(null);
    try {
      await closeHandoff({ handoffId: handoff.id, outcome, reply: outcome === 'returned' ? reply : null });
      menu.setOpen(false);
      onChange?.();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Le relais n’a pas pu être mis à jour.');
    } finally {
      setPending(false);
    }
  };

  return (
    <article className="rounded-lg border border-domino-cardBorder bg-domino-white px-3.5 py-3 transition-colors hover:bg-domino-muted/30 sm:rounded-none sm:border-0">
      <div className="work-action-row">
        <div className="work-account min-w-0">
          <RowIdentity icon={ArrowRightLeft} label={`De ${fromName}`} detail={handoff.contextLabel ?? undefined} />
        </div>
        <div className="work-menu">
          <div ref={menu.ref} className="relative shrink-0">
            <button type="button" aria-label={`Options pour ${handoff.title}`} aria-expanded={menu.open} onClick={() => menu.setOpen(!menu.open)}
              className="inline-flex h-8 w-8 items-center justify-center rounded text-domino-inkSoft hover:bg-domino-muted hover:text-domino-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-domino-prune">
              <MoreHorizontal className="h-4 w-4" />
            </button>
            {menu.open && (
              <div className="absolute right-0 top-9 z-20 w-72 overflow-hidden rounded-2xl border border-domino-cardBorder bg-domino-white shadow-[var(--domino-shadow-elevated)]">
                {replying ? (
                  <form className="space-y-2 p-3" onSubmit={(event) => { event.preventDefault(); void close('returned'); }}>
                    <input autoFocus aria-label="Réponse" value={reply} maxLength={500} onChange={(event) => setReply(event.target.value)}
                      className="w-full rounded-lg border border-domino-cardBorder px-2 py-1.5 text-sm" />
                    <Button type="submit" size="sm" loading={pending} disabled={pending}>Renvoyer à {fromName}</Button>
                  </form>
                ) : (
                  <button type="button" onClick={() => setReplying(true)} className="flex w-full items-center px-3 py-2 text-left text-sm text-domino-ink hover:bg-domino-muted">
                    Renvoyer à {fromName}…
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="work-title min-w-0">
          <h3 className="text-sm font-normal leading-5 text-domino-ink">{handoff.title}</h3>
          {handoff.note && <p className="mt-1 text-xs leading-5 text-domino-inkSoft">« {handoff.note} »</p>}
        </div>
        <div className="work-command flex min-w-0 items-center gap-2">
          {handoff.contextHref && <Link to={handoff.contextHref} className={COMMAND_CLASS}>Ouvrir</Link>}
          <button type="button" disabled={pending} onClick={() => void close('done')} className={COMMAND_CLASS}>Terminé</button>
        </div>
        {error && <p role="alert" className="col-span-full text-sm text-domino-inkSoft">{error}</p>}
      </div>
    </article>
  );
};
